export const MAX_BODIES = 2_000;
export const MAX_LINK_DISTANCE = 2.8;
export const MAX_LINKS_PER_BODY = 5;
export const MIN_BODY_DISTANCE = 0.48;
export const LINK_COMPRESSION_STRENGTH = 48;
export const LINK_TENSION_BREAK_FORCE = LINK_COMPRESSION_STRENGTH * 2.5;
export const WORLD_BOUNDS = {
  minX: -100,
  maxX: 100,
  minY: -260,
  maxY: 140,
} as const;

export type LevelType = "tower" | "bridge" | "archipelago" | "hanging";
export type AnchorGroup = "base" | "start" | "goal" | "support";
export type GroundRect = { x: number; y: number; width: number; height: number };
export type LevelAnchor = Point & { group: AnchorGroup; goalId?: string };
export type LevelGoal = Point & { id: string; reached: boolean };
export type ProgressionState = {
  difficulty: number;
  wins: number;
  startedAt: number;
  lastWinAt: number | null;
  lastDecayAt: number;
};
export type LevelState = {
  type: LevelType;
  round: number;
  title: string;
  goalValue: number;
  grounds: GroundRect[];
  goalLine: { axis: "x" | "y"; value: number };
  source: Point;
  supports?: Point[];
  anchors?: LevelAnchor[];
  goals?: LevelGoal[];
  entry?: Point;
  seed?: number;
  difficulty?: number;
  relief?: number;
  completed: boolean;
  completedBy: string | null;
  nextLevelAt: number | null;
};
export type Point = { x: number; y: number };

export type GooBody = {
  id: number;
  x: number;
  y: number;
  angle: number;
  color: string;
  anchor: boolean;
  owner: string;
  createdAt: number;
  anchorGroup?: AnchorGroup;
  goalId?: string;
};

export type GooLink = {
  id: number;
  a: number;
  b: number;
  length: number;
  strengthVersion?: 2;
};

export type TowerStats = {
  bodyCount: number;
  maxBodies: number;
  linkCount: number;
  activeBodies: number;
  players: number;
  tickMs: number;
  height: number;
  savedAt: number | null;
  level: LevelState;
  progression: ProgressionState;
};

export type ServerMessage =
  | { type: "welcome"; clientId: string; name: string; maxBodies: number }
  | { type: "snapshot"; seq: number; bodies: GooBody[]; links: GooLink[]; stats: TowerStats }
  | { type: "delta"; seq: number; bodies: Array<[number, number, number, number]>; stats?: TowerStats }
  | { type: "stress"; values: Array<[number, number]> }
  | { type: "added"; seq: number; body: GooBody; links: GooLink[]; stats: TowerStats }
  | { type: "removed"; seq: number; bodyIds: number[]; linkIds: number[]; stats: TowerStats }
  | { type: "level_completed"; seq: number; level: LevelState; stats: TowerStats }
  | { type: "level_started"; seq: number; bodies: GooBody[]; links: GooLink[]; stats: TowerStats }
  | { type: "level_updated"; seq: number; bodies: GooBody[]; links: GooLink[]; stats: TowerStats }
  | { type: "reset_vote"; votes: number; required: number; players: number; voted: boolean }
  | { type: "placement_result"; requestId: string; ok: true; bodyId: number }
  | { type: "placement_result"; requestId: string; ok: false; reason: PlacementFailure }
  | { type: "notice"; level: "info" | "warning"; message: string };

export type ClientMessage =
  | { type: "hello"; name?: string }
  | { type: "place"; requestId: string; x: number; y: number }
  | { type: "vote_reset" }
  | { type: "set_stress"; enabled: boolean };

export type PlacementFailure =
  | "invalid_position"
  | "world_full"
  | "too_close"
  | "too_far"
  | "rate_limited"
  | "level_complete"
  | "server_starting";

export type PersistedBody = GooBody & {
  vx: number;
  vy: number;
  angularVelocity: number;
};

export type PersistedWorld = {
  version: 2;
  nextBodyId: number;
  nextLinkId: number;
  level: LevelState;
  progression?: ProgressionState;
  bodies: PersistedBody[];
  links: GooLink[];
  savedAt: number;
};

export type MainToPhysicsMessage =
  | { type: "place"; clientId: string; requestId: string; playerName: string; color: string; x: number; y: number }
  | { type: "snapshot_request"; clientId: string }
  | { type: "client_count"; count: number }
  | { type: "stress_subscribers"; count: number }
  | { type: "reset_level" }
  | { type: "shutdown" };

export type PhysicsToMainMessage =
  | { type: "ready"; stats: TowerStats }
  | { type: "target"; clientId: string; message: ServerMessage }
  | { type: "broadcast"; message: ServerMessage }
  | { type: "stats"; stats: TowerStats }
  | { type: "stress"; values: Array<[number, number]> }
  | { type: "stopped" };
